import { Link } from "react-router-dom";
import {
  BookOpenIcon,
  UserGroupIcon,
  QrCodeIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  WrenchScrewdriverIcon,
  QuestionMarkCircleIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";
import { HelpLayout } from "./components/HelpLayout";
import { HelpSearch } from "./components/HelpSearch";
import { getAllGuides } from "../../lib/help/loadGuides";
import { HELP_CATEGORIES, type HelpCategoryId } from "../../lib/help/types";

const CATEGORY_ICONS: Record<HelpCategoryId, typeof BookOpenIcon> = {
  "getting-started": BookOpenIcon,
  borrowers: UserGroupIcon,
  books: QrCodeIcon,
  operations: ClipboardDocumentListIcon,
  admin: Cog6ToothIcon,
  troubleshooting: WrenchScrewdriverIcon,
};

export function HelpHub() {
  const guides = getAllGuides();
  const popular = guides
    .filter((g) =>
      ["installing-bookburrow", "importing-from-sims-arbor-bromcom", "configuring-automatic-backups", "year-end-promotion"].includes(
        g.frontmatter.slug,
      ),
    )
    .slice(0, 4);

  return (
    <HelpLayout>
      <section className="help-hero relative rounded-3xl bg-forest-900 px-8 py-16 text-cream-50 md:px-16 md:py-20">
        <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-3xl opacity-20 [background:radial-gradient(circle_at_20%_0%,white,transparent_40%),radial-gradient(circle_at_80%_100%,white,transparent_40%)]" />
        <div className="relative z-10 mx-auto max-w-3xl text-center">
          <p className="editorial-label mb-5 text-gold-300">
            BookBurrow help centre
          </p>
          <h1 className="font-display text-4xl font-bold leading-tight md:text-5xl">
            How can we help?
          </h1>
          <p className="mt-4 text-lg text-cream-200/90">
            Step-by-step guides, answers to common questions, and friendly support
            from the people who built BookBurrow.
          </p>
          <div className="mt-8">
            <HelpSearch size="lg" />
          </div>
        </div>
      </section>

      <section className="mt-16">
        <h2 className="font-display text-2xl font-semibold text-bark-900">
          Browse by topic
        </h2>
        <p className="mt-1 text-bark-700">Pick a category to see every guide in that area.</p>

        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {HELP_CATEGORIES.map((cat) => {
            const Icon = CATEGORY_ICONS[cat.id];
            const count = guides.filter((g) => g.frontmatter.category === cat.id).length;
            return (
              <Link
                key={cat.id}
                to={`/help/guides#${cat.id}`}
                className="paper-card group rounded-2xl border border-cream-300 bg-cream-50 p-6 transition-all hover:-translate-y-0.5 hover:border-forest-300 hover:shadow-lg"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-forest-100 text-forest-700 group-hover:bg-forest-600 group-hover:text-cream-50">
                  <Icon className="h-6 w-6" aria-hidden="true" />
                </div>
                <h3 className="mt-4 font-display text-xl font-semibold text-bark-900 group-hover:text-forest-700">
                  {cat.title}
                </h3>
                <p className="mt-1 text-sm text-bark-700">{cat.description}</p>
                <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-forest-700">
                  {count} {count === 1 ? "guide" : "guides"}
                </p>
              </Link>
            );
          })}
        </div>
      </section>

      {popular.length > 0 && (
        <section className="mt-16">
          <h2 className="font-display text-2xl font-semibold text-bark-900">Popular guides</h2>
          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            {popular.map(({ frontmatter }) => (
              <Link
                key={frontmatter.slug}
                to={`/help/guides/${frontmatter.slug}`}
                className="paper-card group flex items-center justify-between gap-4 rounded-xl border border-cream-300 bg-cream-50 px-5 py-4 transition-colors hover:border-forest-300 hover:bg-forest-50"
              >
                <div>
                  <p className="font-display text-lg font-semibold text-bark-900 group-hover:text-forest-700">
                    {frontmatter.title}
                  </p>
                  <p className="mt-0.5 line-clamp-1 text-sm text-bark-700">{frontmatter.description}</p>
                </div>
                <ArrowRightIcon className="h-5 w-5 flex-shrink-0 text-forest-600 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="mt-16 grid gap-5 md:grid-cols-2">
        <Link
          to="/help/guides"
          className="paper-card group flex items-center gap-5 rounded-2xl border border-cream-300 bg-cream-50 p-6 transition-all hover:border-forest-300 hover:shadow-lg"
        >
          <BookOpenIcon className="h-10 w-10 text-forest-600" aria-hidden="true" />
          <div>
            <p className="font-display text-xl font-semibold text-bark-900">All guides</p>
            <p className="text-sm text-bark-700">Browse every walkthrough, grouped by topic.</p>
          </div>
        </Link>
        <Link
          to="/help/faq"
          className="paper-card group flex items-center gap-5 rounded-2xl border border-cream-300 bg-cream-50 p-6 transition-all hover:border-forest-300 hover:shadow-lg"
        >
          <QuestionMarkCircleIcon className="h-10 w-10 text-forest-600" aria-hidden="true" />
          <div>
            <p className="font-display text-xl font-semibold text-bark-900">FAQ</p>
            <p className="text-sm text-bark-700">Quick answers to the questions schools ask most.</p>
          </div>
        </Link>
      </section>
    </HelpLayout>
  );
}
